import React from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import { css } from '@compiled/react';

import Button from '@atlaskit/button/standard-button';
import { Checkbox } from '@atlaskit/checkbox';
import { FormFooter } from '@atlaskit/form';
import Heading from '@atlaskit/heading';
import { Box, Flex, Inline, Stack, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden';

import { AgentAvatar } from './agent-avatar';
import { messages } from '../messages';
import { ALL_CODING_AGENT_IDS, CODING_AGENT_IDS, CodingAgentId } from '../types';

const AGENT_LABEL_MESSAGE: Record<CodingAgentId, typeof messages.cursor> = {
	[CODING_AGENT_IDS.githubCopilot]: messages.githubCopilot,
	[CODING_AGENT_IDS.claude]: messages.claude,
	[CODING_AGENT_IDS.cursor]: messages.cursor,
	[CODING_AGENT_IDS.codex]: messages.codex,
};

export interface LeftHandSideCodingAgentChoiceProps {
	readonly selected: CodingAgentId[];
	readonly onToggle: (id: CodingAgentId) => void;
	readonly onClickNext: () => void;
}

/**
 * TWCG-1935: left-hand side of the coding-agent-choice screen.
 *
 * Implements the Figma design (JFIND-Q4FY26, node 8189-62473):
 *  - Heading + sub-text
 *  - Vertical stack of {@link AgentCard}s — each card is a row with the
 *    agent avatar, name (+ optional subtitle for Rovo Dev) and a trailing
 *    multi-select checkbox.
 *  - Footer with a single primary "Next" CTA on the right.
 */
export const LeftHandSideCodingAgentChoice: React.FC<LeftHandSideCodingAgentChoiceProps> = ({
	selected,
	onToggle,
	onClickNext,
}) => {
	return (
		<Flex direction="column" justifyContent="space-between" xcss={containerStyles}>
			<Stack space="space.400">
				<Stack space="space.150">
					<Heading as="h1" size="xlarge">
						<FormattedMessage {...messages.heading} />
					</Heading>
					<Box xcss={subtextStyles}>
						<FormattedMessage {...messages.description} />
					</Box>
				</Stack>
				<Stack space="space.100">
					{ALL_CODING_AGENT_IDS.map((id) => (
						<AgentCard key={id} id={id} isChecked={selected.includes(id)} onToggle={onToggle} />
					))}
				</Stack>
			</Stack>
			<FormFooter>
				<Flex justifyContent="end" alignItems="center" xcss={footerStyles}>
					<Button
						type="button"
						appearance="primary"
						onClick={onClickNext}
						testId="coding-agent-choice-next"
					>
						<FormattedMessage {...messages.nextButton} />
					</Button>
				</Flex>
			</FormFooter>
		</Flex>
	);
};

interface AgentCardProps {
	readonly id: CodingAgentId;
	readonly isChecked: boolean;
	readonly onToggle: (id: CodingAgentId) => void;
}

const AgentCard: React.FC<AgentCardProps> = ({ id, isChecked, onToggle }) => {
	const { formatMessage } = useIntl();
	const agentName = formatMessage(AGENT_LABEL_MESSAGE[id]);
	const checkboxLabel = formatMessage(messages.checkboxLabel, {
		agent: agentName,
	});

	return (
		<Box
			xcss={[cardStyles, isChecked && cardSelectedStyles]}
			testId={`coding-agent-option-${id}`}
			onClick={() => onToggle(id)}
		>
			<Flex
				alignItems="center"
				justifyContent="space-between"
				columnGap="space.150"
				xcss={cardInnerStyles}
			>
				<Inline space="space.200" alignBlock="center" grow="fill">
					<AgentAvatar agentId={id} />
					<span css={agentNameCss}>{agentName}</span>
				</Inline>
				<div css={cardCursorCss}>
					<Checkbox
						isChecked={isChecked}
						onChange={() => {}}
						label={<VisuallyHidden>{checkboxLabel}</VisuallyHidden>}
						testId={`coding-agent-checkbox-${id}`}
					/>
				</div>
			</Flex>
		</Box>
	);
};

const containerStyles = xcss({
	height: '100%',
});

const subtextStyles = xcss({
	color: 'color.text.subtle',
	font: 'font.body',
});

const cardStyles = xcss({
	backgroundColor: 'elevation.surface',
	borderRadius: 'border.radius.200',
	borderWidth: 'border.width.outline',
	borderStyle: 'solid',
	borderColor: 'color.border',
	cursor: 'pointer',
	':hover': {
		backgroundColor: 'color.background.neutral.subtle.hovered',
	},
});

const cardSelectedStyles = xcss({
	borderColor: 'color.border.selected',
});

const cardInnerStyles = xcss({
	paddingBlock: 'space.200',
	paddingInline: 'space.200',
});

const cardCursorCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-important-styles
	'& *': {
		cursor: 'pointer !important',
	},
});

const agentNameCss = css({
	color: token('color.text'),
	font: token('font.body'),
	fontWeight: token('font.weight.medium'),
});

const footerStyles = xcss({
	width: '100%',
	color: 'color.text.subtlest',
});
